import {ClientPortalModulePermissionCodes} from 'utils/types';
import {defineTabDef} from 'utils/helpers';
import {useProfile} from 'utils/useProfile';

export const clientProfileTabs = defineTabDef({
  'business-details': {
    tabName: 'business-details',
    tabTitle: 'Business Details',
    requiredAnyPermission: [ClientPortalModulePermissionCodes.view_client_business_details],
  },
  invoices: {
    tabName: 'invoices',
    tabTitle: 'Invoices',
    // Separate from the profile permission on purpose: a client user can be allowed to see the
    // company record without being allowed to see billing.
    requiredAnyPermission: [ClientPortalModulePermissionCodes.view_client_invoices],
  },
  'monthly-summary': {
    tabName: 'monthly-summary',
    tabTitle: 'Monthly Summary',
    // The same permission as the Invoices tab: this is the same billing data, cut by month, so
    // there is nothing here to grant separately.
    requiredAnyPermission: [ClientPortalModulePermissionCodes.view_client_invoices],
  },
});

export type ClientProfileTabName = keyof typeof clientProfileTabs;

/**
 * How the page's own sidebar lays the tabs out. Kept apart from the tab definitions above
 * because `TabDefinition` is the shape every page's guard and router share - this grouping is
 * only about this page's navigation.
 */
export const clientProfileNavGroups: {title: string; tabs: ClientProfileTabName[]}[] = [
  {title: 'Profile', tabs: ['business-details']},
  {title: 'Payments', tabs: ['invoices', 'monthly-summary']},
];

/** Tabs in sidebar order, which is also the order a default tab is picked in. */
export const clientProfileTabOrder: ClientProfileTabName[] = clientProfileNavGroups.flatMap(group => group.tabs);

/**
 * One line per tab explaining what it is for, so the content below needs no preamble. Worded for
 * both audiences: staff read these while looking at someone else's client, so no "your company".
 */
export const clientProfileTabSubtitles: Record<ClientProfileTabName, string> = {
  'business-details':
    'The company record as Pathformance holds it. Keeping it current is what makes sure invoices and statements reach the right place.',
  invoices:
    'Every campaign invoice, always available here. “View invoice” opens the same secure invoice page emailed to the billing contacts, and “Pay now” goes straight to online payment for any open balance.',
  'monthly-summary':
    "Pull a month's activity by whichever date matters — campaign flight dates or invoice dates — and hand it straight to the AP team.",
};

/** The sidebar group a tab sits under, which is also the section the breadcrumb names. */
export const clientProfileTabGroup = (tab: ClientProfileTabName): string =>
  clientProfileNavGroups.find(group => group.tabs.includes(tab))?.title ?? '';

/**
 * Whether the user may browse any client's profile rather than only their own. Mirrors the
 * backend's `get_accessible_client_ids`: the permission only widens scope for staff.
 */
export const useCanPickClient = (): boolean => {
  const {profile, hasAnyPermission} = useProfile();
  return (
    Boolean(profile?.is_staff || profile?.is_superuser) &&
    hasAnyPermission([ClientPortalModulePermissionCodes.view_any_client_profile], null)
  );
};
